import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { AuthStatus } from '../components/auth/AuthStatus';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Settings, Globe, Info, Volume2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { TTSService } from '../services/tts';
import type { TTSVoice } from '../types/voice';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [voices, setVoices] = useState<TTSVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>(TTSService.getSelectedVoiceCode() || '');
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [autoSpeak, setAutoSpeak] = useLocalStorage<boolean>('kiosk-auto-speak', false);

  // Handle language change
  const handleLanguageChange = (newLanguage: 'vi' | 'en') => {
    setLanguage(newLanguage);
  };

  useEffect(() => {
    const loadVoices = async () => {
      try {
        setLoadingVoices(true);
        const list = await TTSService.listVoices({ voiceOwnership: 'VBEE', languageCode: 'vi-VN' });
        setVoices(list);
        if (!selectedVoice && list.length > 0) {
          setSelectedVoice(list[0].code);
        }
      } catch (e) {
        setVoiceError(e instanceof Error ? e.message : 'Không thể tải danh sách giọng đọc');
      } finally {
        setLoadingVoices(false);
      }
    };
    loadVoices();
  }, []);

  const handleVoiceSelect = (code: string) => {
    setSelectedVoice(code);
    TTSService.setSelectedVoiceCode(code);
  };

  return (
    <div className="h-screen flex flex-col bg-kiosk-background">
      {/* Header */}
      <div className="bg-white shadow-sm border-b shrink-0">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="lg"
                onClick={() => navigate('/')}
                className="kiosk-touch-target"
              >
                <ArrowLeft className="h-6 w-6 mr-2" />
                {t('common.back')}
              </Button>
              <div className="flex items-center gap-2">
                <Settings className="h-6 w-6 text-kiosk-primary" />
                <h1 className="text-2xl font-bold text-kiosk-base">
                  {t('settings.title')}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto space-y-6">
          
          {/* Authentication Status */}
          <AuthStatus />
          
          {/* Language Settings */}
          <Card className="kiosk-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Globe className="h-5 w-5" />
                {t('settings.language.title')}
              </CardTitle>
              <CardDescription>
                {t('settings.language.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-kiosk-muted">
                    {t('settings.language.current')}
                  </label>
                  <Select value={language} onValueChange={handleLanguageChange}>
                    <SelectTrigger className="w-full kiosk-touch-target">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vi">Tiếng Việt</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* System Information */}
          <Card className="kiosk-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Info className="h-5 w-5" />
                {t('settings.system.title')}
              </CardTitle>
              <CardDescription>
                {t('settings.system.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-kiosk-muted">
                    {t('settings.system.version')}
                  </label>
                  <div className="text-kiosk-base">
                    v1.0.0
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-kiosk-muted">
                    {t('settings.system.apiStatus')}
                  </label>
                  <div>
                    <Badge variant="default" className="text-sm">
                      {t('settings.system.connected')}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* TTS Voice Settings */}
          <Card className="kiosk-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Volume2 className="h-5 w-5" />
                {t('settings.tts.title')}
              </CardTitle>
              <CardDescription>
                {t('settings.tts.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-kiosk-muted">
                    {t('settings.tts.currentVoice')}
                  </label>
                  <Select value={selectedVoice} onValueChange={handleVoiceSelect}>
                    <SelectTrigger className="w-full kiosk-touch-target">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingVoices && <div className="p-2 text-sm">Đang tải...</div>}
                      {voiceError && <div className="p-2 text-sm text-red-600">{voiceError}</div>}
                      {!loadingVoices && !voiceError && voices.map(v => (
                        <SelectItem key={v.code} value={v.code}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-kiosk-muted">
                    {t('settings.tts.autoSpeak.title')}
                  </label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="checkbox"
                      checked={!!autoSpeak}
                      onChange={e => setAutoSpeak(e.target.checked)}
                      className="w-6 h-6 kiosk-touch-target"
                    />
                    <Label className="text-kiosk-base">
                      {t('settings.tts.autoSpeak.description')}
                    </Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;